import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, firstValueFrom, filter } from 'rxjs';
import { AuthService } from '@auth';
import { TranslateService } from '@ngx-translate/core';
import { DexieDbProvider } from '@shared/services/dexie-provider';
import { Organization } from '@shared/models/user-profile';
import { emptyUnsyncedSummary, PreferredOrganizationSource, UnsyncedSummary } from '@shared/models/preferred-organization';
import { FirestoreService } from './firestore.service';
import { OnlineOfflineService } from './online-offine.service';
import { PreferredOrganizationService } from './preferred-organization.service';
import { UnsyncedRecordsService } from './unsynced-records.service';

export type PreferredOrgChangeStepId = 'sync' | 'clear' | 'download' | 'setDefault';
export type PreferredOrgChangeStepStatus = 'pending' | 'running' | 'done' | 'failed';

export interface PreferredOrgChangeStep {
  id: PreferredOrgChangeStepId;
  status: PreferredOrgChangeStepStatus;
}

export interface PreferredOrgChangeProgress {
  /** Only the applicable steps, in execution order. */
  steps: PreferredOrgChangeStep[];
  targetName: string;
  oldName?: string;
  completed: boolean;
  /** Translation key describing why the change stopped. */
  errorKey?: string;
}

export interface PreferredOrgChangeGate {
  allowed: boolean;
  summary: UnsyncedSummary;
}

/**
 * Headless engine for selecting or changing the browser's preferred organization
 * (US 27702). Both the first-login picker and the organizations menu drive it; the
 * progress lives here so "Run in background" just closes the UI.
 *
 * Step order (the UI renders the same four steps the wireframe shows):
 *   1. sync        — push every pending local record of the old preferred organization
 *                    (only when something is pending and the signed-in user may sync it).
 *   2. clear       — wipe Dexie, the Firestore offline cache and the persisted
 *                    pending flag (only when this browser holds local data).
 *   3. download    — switch the active organization to the target, persist it as
 *                    preferred (crash-safe: an interrupted download resumes on the
 *                    next login), and pull its data into this browser.
 *   4. setDefault  — the record persisted in step 3 is what makes it open by default.
 *
 * The flow ends with a full document navigation: Firestore's terminate() leaves the
 * page's instance unusable, and a clean reload re-initializes everything against the
 * new preferred organization. The completion toast survives the reload through the
 * existing 'auth_message' stored-message mechanism.
 */
@Injectable({
  providedIn: 'root'
})
export class PreferredOrganizationChangeService {

  public progress$ = new BehaviorSubject<PreferredOrgChangeProgress | undefined>(undefined);

  constructor(
    private authService: AuthService,
    private onlineOfflineService: OnlineOfflineService,
    private preferredOrganizationService: PreferredOrganizationService,
    private unsyncedRecordsService: UnsyncedRecordsService,
    private injector: Injector,
    private firestoreService: FirestoreService,
    private localDbProvider: DexieDbProvider,
    private translateService: TranslateService
  ) { }

  /**
   * PullDataService / PushDataService sit on top of the app's largest module graphs
   * (invoicing, purchasing, replication, server-driven tables). Importing them
   * eagerly here — a service reachable from CoreModule's components — created a
   * circular evaluation order that broke DI at bootstrap, so they are loaded at
   * call time instead.
   */
  private async pushDataServiceAsync() {
    const { PushDataService } = await import('./push-data.service');
    return this.injector.get(PushDataService);
  }

  private async pullDataServiceAsync() {
    const { PullDataService } = await import('./pull-data.service');
    return this.injector.get(PullDataService);
  }

  public get inProgress(): boolean {
    const progress = this.progress$.value;
    return !!progress && !progress.completed && !progress.errorKey;
  }

  /**
   * Whether the preferred organization may be changed right now: online, no change
   * already running, and nothing pending (unsynced records, open registers or
   * unsynced closures) across ALL users of this browser.
   */
  public async canChangeNowAsync(): Promise<PreferredOrgChangeGate> {
    if (!this.onlineOfflineService.isOnline || this.inProgress) {
      return { allowed: false, summary: emptyUnsyncedSummary() };
    }

    const preferred = this.preferredOrganizationService.preferred;
    if (!preferred) return { allowed: true, summary: emptyUnsyncedSummary() };

    if (!this.preferredOrganizationService.isPreferredPermittedFor(this.authService.userOrganizations)) {
      // Not permitted for the preferred tenant: the persisted flag is the only signal.
      const summary = this.unsyncedRecordsService.summarizePersistedState(
        this.unsyncedRecordsService.readPersistedState());
      return { allowed: summary.total === 0, summary };
    }

    const summary = await this.unsyncedRecordsService.getGateSummaryAsync();
    return { allowed: summary.total === 0, summary };
  }

  /**
   * Run the whole change. Requires a connection. Resolves only if the flow fails
   * before its final full-page navigation — on success the page reloads.
   */
  public async changePreferredAsync(target: Organization, source: PreferredOrganizationSource): Promise<void> {
    if (this.inProgress) return;
    if (!this.onlineOfflineService.isOnline) {
      this.failBeforeStart(target, 'preferredOrg.errors.offline');
      return;
    }
    if (!target?.keycloakOrgId) {
      this.failBeforeStart(target, 'preferredOrg.errors.unknownOrganization');
      return;
    }

    const oldPreferred = this.preferredOrganizationService.preferred;
    const userMaySyncOld = !!oldPreferred &&
      this.preferredOrganizationService.isPreferredPermittedFor(this.authService.userOrganizations);

    // A permitted user gets a LIVE answer (the persisted flag alone could be stale
    // at 0 and let the clear step destroy real unsynced records); an unpermitted
    // one can only be judged by the flag.
    const pendingTotal = userMaySyncOld
      ? (await this.unsyncedRecordsService.getGateSummaryAsync()).total
      : this.unsyncedRecordsService.aggregatePersistedTotal(this.unsyncedRecordsService.readPersistedState());

    const needsSync = !!oldPreferred && userMaySyncOld && pendingTotal > 0;
    if (!!oldPreferred && !userMaySyncOld && pendingTotal > 0) {
      // Another user's records are still pending and this user cannot sync them.
      this.failBeforeStart(target, 'preferredOrg.errors.pendingFromOtherUsers');
      return;
    }

    const hasLocalData = !!oldPreferred || await this.hasAnyLocalDataAsync();
    const steps: PreferredOrgChangeStep[] = [
      ...(needsSync ? [{ id: 'sync' as const, status: 'pending' as const }] : []),
      ...(hasLocalData ? [{ id: 'clear' as const, status: 'pending' as const }] : []),
      { id: 'download', status: 'pending' },
      { id: 'setDefault', status: 'pending' }
    ];

    const progress: PreferredOrgChangeProgress = {
      steps,
      targetName: target.name,
      oldName: oldPreferred?.name,
      completed: false
    };
    this.progress$.next(progress);

    try {
      if (needsSync) {
        await this.runStep(progress, 'sync', async () => {
          // Pushing needs the old preferred organization's token (TenantId header
          // and the tenant-scoped Firebase session).
          if (oldPreferred && this.authService.currentTenant !== oldPreferred.tenantId) {
            await this.authService.switchTenantAsync(oldPreferred.keycloakOrgId, { navigateHome: false });
          }
          await (await this.pushDataServiceAsync()).pushAllPendingNowAsync();

          const after = await this.unsyncedRecordsService.getGateSummaryAsync();
          if (after.total > 0) {
            throw new Error('preferredOrg.errors.syncIncomplete');
          }
        });
      }

      if (hasLocalData) {
        await this.runStep(progress, 'clear', async () => {
          this.unsyncedRecordsService.clearPersistedState();
          await this.localDbProvider.deleteDatabase();
          await this.firestoreService.clearOfflineCacheBestEffort();
        });
      }

      await this.runStep(progress, 'download', async () => {
        if (this.authService.currentTenant !== target.tenantId || !target.tenantId) {
          await this.authService.switchTenantAsync(target.keycloakOrgId, { navigateHome: false });
        }

        // Persist BEFORE the pull: if the download is interrupted, the next login
        // resumes it incrementally instead of forgetting the choice.
        const active = this.authService.currentOrganization;
        if (!active?.tenantId) {
          throw new Error('preferredOrg.errors.switchFailed');
        }
        this.preferredOrganizationService.setPreferred(active, source);

        const pullDataService = await this.pullDataServiceAsync();
        const completed = firstValueFrom(
          pullDataService.syncStatusChanged.pipe(filter((done: boolean) => done === true))
        );
        pullDataService.requestSync();
        await completed;
      });

      await this.runStep(progress, 'setDefault', async () => {
        // The record persisted during the download is what opens this organization
        // by default; nothing further to do.
      });

      progress.completed = true;
      this.progress$.next({ ...progress });
      this.finishWithReload(target.name);
    } catch (error: any) {
      const key = typeof error?.message === 'string' && error.message.startsWith('preferredOrg.')
        ? error.message
        : 'preferredOrg.errors.changeFailed';
      this.progress$.next({ ...progress, errorKey: key });
    }
  }

  /** Clear a finished or failed run so the UI can start fresh. */
  public resetProgress(): void {
    if (!this.inProgress) {
      this.progress$.next(undefined);
    }
  }

  private async runStep(
    progress: PreferredOrgChangeProgress,
    id: PreferredOrgChangeStepId,
    work: () => Promise<void>
  ): Promise<void> {
    const step = progress.steps.find(s => s.id === id);
    if (!step) return;

    step.status = 'running';
    this.progress$.next({ ...progress });
    try {
      await work();
      step.status = 'done';
      this.progress$.next({ ...progress });
    } catch (error) {
      step.status = 'failed';
      this.progress$.next({ ...progress });
      throw error;
    }
  }

  private async hasAnyLocalDataAsync(): Promise<boolean> {
    try {
      const count = await this.localDbProvider.db.syncMetadata.count();
      return count > 0;
    } catch {
      return false;
    }
  }

  private failBeforeStart(target: Organization | undefined, errorKey: string): void {
    this.progress$.next({
      steps: [],
      targetName: target?.name ?? '',
      completed: false,
      errorKey
    });
  }

  /**
   * End with a full document load: it re-creates the terminated Firestore instance
   * against the cleared cache, and the stored message shows the completion toast
   * after the reload (AuthService.checkAndDisplayStoredMessage).
   */
  private finishWithReload(targetName: string): void {
    try {
      const message = this.translateService.instant('preferredOrg.toast.nowLocal', { org: targetName });
      localStorage.setItem('auth_message', message);
    } catch {
      // Losing the toast is acceptable; losing the reload is not.
    }
    window.location.assign('/');
  }

}
